import { useEffect, useState } from 'react'
import { BarChart3, Users, Trophy, Award, Radio, Package, RefreshCw } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import PageHeader from '@/components/PageHeader'
import { useAuth } from '@/hooks/useAuth'
import api from '@/lib/api'

const POLL_MS = 30_000

export default function AnalyticsPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  const load = async () => {
    setRefreshing(true)
    try { const r = await api.get('/admin/analytics'); setData(r.data); setError(null) }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
    finally { setLoading(false); setRefreshing(false) }
  }

  useEffect(() => {
    if (!isAdmin) return
    load()
    const iv = setInterval(load, POLL_MS)
    return () => clearInterval(iv)
  }, [isAdmin])

  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>
  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>

  const v = data?.vision
  const maxBadge = Math.max(1, ...(data?.badges ?? []).map(b => b.awarded))

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        icon={BarChart3}
        eyebrow="Beheer"
        title="Analytics"
        subtitle="Live overzicht van gebruik en engagement"
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
          </Button>
        }
      />

      {error && <Card className="border-red-500/40"><CardContent className="p-3 text-sm text-red-500">⚠ {error}</CardContent></Card>}

      {/* Top stats row */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={Users}    label="Users totaal" value={data?.users?.total ?? 0}
                  hint={`+${data?.users?.recent7d ?? 0} laatste 7d`} />
        <StatCard icon={Trophy}  label="Tournaments"
                  value={data?.tournaments?.total ?? 0}
                  hint={`${data?.tournaments?.active ?? 0} actief`} />
        <StatCard icon={Radio} label="Vision inzendingen"
                  value={v?.stats?.submissions ?? 0}
                  hint={`${v?.stats?.approved ?? 0} approved`} />
        <StatCard icon={Award}    label="Publiek stemmers"
                  value={v?.stats?.public_voters ?? 0}
                  hint={`${v?.stats?.public_votes ?? 0} stemmen`} />
      </div>

      {/* Vision detail */}
      {v?.show && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Radio className="h-4 w-4" /> {v.show.name}{v.show.edition ? ` — ${v.show.edition}` : ''}
              <Badge variant="outline" className="ml-2 text-xs uppercase">{v.show.phase}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <MiniStat label="Entries live"      value={v.stats?.entries ?? 0} />
              <MiniStat label="Jury's aangemaakt" value={v.stats?.juries ?? 0} />
              <MiniStat label="Jury's ingediend"  value={`${v.stats?.juries_locked ?? 0} / ${v.stats?.juries ?? 0}`} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modules per category */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Package className="h-4 w-4" /> Modules per categorie</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data?.modules ?? []).map((m, i) => (
            <div key={i} className="text-sm">
              <div className="flex justify-between mb-0.5">
                <span className="capitalize">{m.category || 'anders'}</span>
                <span className="text-muted-foreground">{m.enabled}/{m.total} enabled</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-cyan-500 transition-all" style={{ width: `${(m.enabled/m.total)*100}%` }} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Badges awarded */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Award className="h-4 w-4" /> Badges toegekend</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {(data?.badges ?? []).length === 0 && <p className="text-sm text-muted-foreground">Nog geen badges uitgereikt.</p>}
          {(data?.badges ?? []).map((b, i) => (
            <div key={i} className="text-sm">
              <div className="flex justify-between mb-0.5">
                <span className="truncate flex-1">{b.name}</span>
                <span className="tabular-nums text-muted-foreground ml-2">{b.awarded}×</span>
              </div>
              <div className="h-1 rounded-full bg-muted overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${(b.awarded/maxBadge)*100}%`, background: b.color || '#3b82f6' }} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {data?.ts && (
        <div className="text-xs text-muted-foreground text-right">
          Laatst gerefresht: {new Date(data.ts).toLocaleTimeString('nl-BE')}
        </div>
      )}
    </div>
  )
}

function StatCard({ icon: Icon, accent, label, value, hint }) {
  const grad = {
    blue:    'from-blue-500 to-indigo-600',
    orange:  'from-orange-500 to-red-500',
    magenta: 'from-pink-500 to-purple-600',
    gold:    'from-yellow-400 to-orange-500',
  }[accent] || 'from-slate-500 to-slate-700'
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${grad} flex items-center justify-center shrink-0`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
            <div className="text-2xl font-bold tabular-nums">{value}</div>
            {hint && <div className="text-xs text-muted-foreground truncate">{hint}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function MiniStat({ label, value }) {
  return (
    <div className="p-3 rounded border border-border bg-card">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
    </div>
  )
}
